import { Link } from "react-router-dom";
import { ArrowRight, Bell, LayoutGrid, Clock } from "lucide-react";
import { EmptyState } from "@/design/components";
import { useSeed, useProgrammeSummaries, useMoney } from "@/domain/hooks";
import { useDealCards } from "@/screens/shared/dealCards";
import { DealCard } from "@/screens/stratus/DealCard";
import { cn } from "@/design/cn";

/**
 * The funder portal home is the same segregated deal-card grid as Stratus, with one
 * funder-specific guard: staged visibility. Deals not yet shared with the funder render
 * as "access pending" cards — no figures, no click — exactly mirroring the previous
 * dashboard. Shared, active deals are clickable into their workspace; shared deals still
 * in feasibility sit in the muted "Preparing" band. The funder's one cross-cutting block,
 * the "Awaiting your action" disbursement queue, stays above the grid.
 */
export function FunderDashboardHome() {
  const seed = useSeed();
  const money = useMoney();
  const programmes = useProgrammeSummaries();
  const cards = useDealCards();

  const inQueue = seed.disbursements.filter((d) => d.state === "ready_for_funder");

  // Visibility lives on the programme summary, keyed by programme id; zip it onto each card.
  const visById = new Map(programmes.map((p) => [p.id, p.funderVisibility] as const));
  const isShared = (id: string) => visById.get(id) !== "not_shared";

  // Three groups: shared + active (clickable), shared + feasibility (Preparing band),
  // and not_shared (access pending — no figures, no click), independent of phase.
  const sharedActive = cards.filter((c) => isShared(c.id) && c.isActive);
  const preparing = cards.filter((c) => isShared(c.id) && !c.isActive);
  const pending = cards.filter((c) => !isShared(c.id));

  return (
    <div className="space-y-7">
      {/* Title row */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-ink">Your portfolio</h1>
        <p className="mt-1 text-sm text-mute">
          <span className="tabular-nums font-medium text-ink">{sharedActive.length + preparing.length}</span> active deals
          {pending.length > 0 && (
            <>
              <span className="mx-1.5 text-faint" aria-hidden>·</span>
              <span className="tabular-nums">{pending.length}</span> in preparation
            </>
          )}
        </p>
      </div>

      {/* Disbursements awaiting funder decision */}
      <section>
        <SectionLabel icon={Bell} label="Awaiting your decision">
          <Link to="/funder/approvals" className="flex items-center gap-1 text-xs font-medium text-brand hover:underline">
            All approvals <ArrowRight className="h-3 w-3" />
          </Link>
        </SectionLabel>
        {inQueue.length === 0 ? (
          <EmptyState title="Nothing in your queue" body="Routed milestone disbursements appear here for your decision." />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {inQueue.map((d) => {
              const ms = seed.milestones.find((m) => m.id === d.milestoneId);
              const basis = (d.basisAskIds ?? []).map((id) => seed.spendRequests.find((r) => r.id === id)).filter(Boolean);
              return (
                <div key={d.id} className="overflow-hidden rounded-xl border border-warn/30 bg-warnSoft">
                  <div className="h-[3px] bg-warn" />
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5 shrink-0 text-warnStrong" />
                        <span className="text-xs font-semibold uppercase tracking-wide text-warnStrong">
                          Milestone disbursement
                        </span>
                      </div>
                      <span className="font-mono text-xl font-bold tabular-nums tracking-tight text-ink">
                        {money(d.amount)}
                      </span>
                    </div>
                    <p className="mt-2.5 text-sm font-semibold text-ink">{ms?.name ?? "Milestone"}</p>
                    <p className="mt-1 text-xs leading-snug text-mute">
                      Backed by {basis.length} confirmed spend ask{basis.length === 1 ? "" : "s"} with evidence attached.
                    </p>
                    <Link
                      to="/funder/approvals"
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-warn/25 bg-warn/10 px-3 py-1.5 text-xs font-semibold text-warnStrong hover:bg-warn/20 transition-colors"
                    >
                      Review and approve <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Active, shared deal-card grid */}
      {sharedActive.length > 0 && (
        <section>
          <SectionLabel icon={LayoutGrid} label={`Active deals · ${sharedActive.length}`} />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {sharedActive.map((c) => (
              <DealCard key={c.id} data={c} to={`/workspace/${c.workspaceId}`} from="/funder" />
            ))}
          </div>
        </section>
      )}

      {/* Not-shared deals: access pending */}
      {pending.length > 0 && (
        <section>
          <SectionLabel label="Access pending" muted>
            <p className="text-xs text-faint">Stratus will notify you when these are ready.</p>
          </SectionLabel>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {pending.map((c) => (
              <div key={c.id} className="w-72 shrink-0">
                <DealCard data={c} to="" accessPending />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Shared deals in feasibility */}
      {preparing.length > 0 && (
        <section>
          <SectionLabel label={`In pipeline · ${preparing.length}`} muted />
          <div className="flex gap-3 overflow-x-auto pb-2">
            {preparing.map((c) => (
              <div key={c.id} className="w-72 shrink-0">
                <DealCard data={c} to="" disabled />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SectionLabel({
  icon: Icon,
  label,
  muted,
  children,
}: {
  icon?: React.ElementType;
  label: string;
  muted?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className={cn("mb-3 flex items-center gap-2", children ? "justify-between" : "")}>
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-faint" />}
        <p className={cn("shrink-0 text-[11px] font-semibold uppercase tracking-widest", muted ? "text-faint" : "text-mute")}>
          {label}
        </p>
        {!children && <div className="h-px flex-1 bg-hairline" />}
      </div>
      {children && <div className="flex items-center gap-1">{children}</div>}
    </div>
  );
}
